import React, { useEffect, useState, useMemo, useCallback, useReducer } from 'react';
import Slider from 'react-slick';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import classnames from 'classnames';

import callApi from '../../utils/callApi';
import {
  ANCHOR_FEATURED_ALL_CLIPSTORE_ITEMS_FOR_SALE,
  ARHIVE_LIST_BY_SEGMENT,
  FEATURED_LIST_ALL,
  FREEMIUM_USER_ALL_FREE_CONTENT,
} from '../../constants/apiUrls/apiUrls';
import FeaturedItem from '../featuredComponents/FeaturedItem';

import './GuestHomeFeaturedSection.css';
import SkeletonLoader from '../Loaders/SkeletonLoader';

import styles from './css/GuestHomeFeaturedSection.module.css';


const NUMBER_OF_ITEMS_TO_FETCH = 15;
const DEFAULT_SEGMENT_ID = 34;

function GuestHomeFeaturedSection(props) {
  const { type, setSelectedVideo, setSegmentId, segmentId } = props;

  const featuredItemsReducer = (state, action) => {
    switch (action.type) {
      case 'add':
        return [...state, ...action.payload];
      case 'set':
        return action.payload;
      default:
        return state;
    }
  };

  const [featuredItems, dispatch] = useReducer(featuredItemsReducer, []);
  const [size, setSize] = useState(NUMBER_OF_ITEMS_TO_FETCH);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const getUrlByType = useCallback(() => {
    switch (type) {
      case 'featured':
        return FEATURED_LIST_ALL;

      case 'past_clips':
        return FREEMIUM_USER_ALL_FREE_CONTENT;

      case 'recommended_clips':
        return ARHIVE_LIST_BY_SEGMENT;

      case 'clipstore':
        return ANCHOR_FEATURED_ALL_CLIPSTORE_ITEMS_FOR_SALE;

      default:
        return 'featured';
    }
  }, [type]);

  const getTitle = useMemo(() => {
    switch (type) {
      case 'featured':
        return 'Featured Content';

      case 'past_clips':
        return 'Past Clips Of The Month';

      case 'recommended_clips':
        return 'Recommended Videos';

      case 'clipstore':
        return 'Clip Store';

      default:
        return 'Featured Content';
    }
  }, [type]);

  const setFeaturedItemsState = useCallback((newData) => {
    switch (type) {
      case 'featured':
        dispatch({ type: 'add', payload: newData });
        break;
      case 'recommended_clips':
        dispatch({ type: 'add', payload: newData.segments });
        break;
      case 'past_clips':
        dispatch({ type: 'add', payload: newData.programs });
        break;
      case 'clipstore':
        dispatch({ type: 'add', payload: newData.content });
        setTotalPages(newData.totalPages);
        break;
      default:
        dispatch({ type: 'add', payload: newData });
    }
  }, [type]);

  const fetchFeaturedItemsList = useCallback((size, pageNum) => {
    const pageSize = type === 'clipstore' ? NUMBER_OF_ITEMS_TO_FETCH : size;

    if (type === 'clipstore' && (pageNum === totalPages - 1)) {
      return;
    }

    const urlBySectionType = getUrlByType();
    let url = urlBySectionType.
      replace('{page}', pageNum).
      replace('{size}', pageSize);
    if (type === 'recommended_clips') {
      url = url.replace('{id}', segmentId || DEFAULT_SEGMENT_ID);
    }
    callApi(url, 'get', {}).then((res) => {
      setIsLoading(false);
      setFeaturedItemsState(res.data);

      if (setSegmentId) {
        setSegmentId(res.data?.programs?.[0]?.programSegments?.[0]?.programSegmentId);
      }
      if (type === 'past_clips' && !props.selectedVideo?.id) {
        setSelectedVideo(res.data?.programs?.[0]);
        if (setSegmentId) {
          setSegmentId(res.data?.programs?.[0]?.clip?.programSegments?.[0]?.segment?.id);
        }
      }
    });
  }, [getUrlByType, props.selectedVideo?.id, segmentId, setFeaturedItemsState, setSegmentId, setSelectedVideo, totalPages, type]);

  const handleIndexChange = useCallback(
    (index) => {
      if (index >= (((page + 1) * size) - 10)) {
        const newSize = size || NUMBER_OF_ITEMS_TO_FETCH;
        const newPage = page + 1;
        setSize(newSize);
        setPage(newPage);
        fetchFeaturedItemsList(newSize, newPage);
      }
    },
    [size, page, fetchFeaturedItemsList]
  );

  useEffect(() => {
    fetchFeaturedItemsList(NUMBER_OF_ITEMS_TO_FETCH, 0);
  }, [fetchFeaturedItemsList]);

  const sliderStyle = classnames('guest-featured-slider', 'anchors-slider', {
    'clipstore': type === 'clipstore',
    'featured': type === 'featured',
    'recommended_clips': type === 'recommended_clips' || type === 'past_clips',
  });

  const renderFeaturedItem = useCallback(
    (featuredItem) => (
      <FeaturedItem
        key={featuredItem.name || featuredItem.title}
        featuredItem={featuredItem}
        setSegmentId={setSegmentId}
        setSelectedVideo={setSelectedVideo}
        type={type}
      />
    ),
    [setSegmentId, setSelectedVideo, type]
  );

  if (isLoading) {

    return (
      <React.Fragment>
        <div className="anchors-section-header">
          <div className="header-wrapper">
            <div className={styles.headerLine} />
            <h1>{getTitle}</h1>
            <div className={styles.headerLine} />
          </div>
        </div>
        <div className="anchors-slider-loader">
          <SkeletonLoader
            count={20}
          />
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="anchors-section-header">
        <div className="header-wrapper">
          <div className={styles.headerLine} />
          <h1 className="header-white">{getTitle}</h1>
          <div className={styles.headerLine} />
        </div>
      </div>
      <Slider
        afterChange={handleIndexChange}
        arrows={true}
        centerMode={false}
        centerPadding={10}
        className={sliderStyle}
        infinite={false}
        initialSlide={0}
        lazyLoad={false}
        nextArrow={<ChevronRightRoundedIcon />}
        prevArrow={<ChevronLeftRoundedIcon />}
        responsive={[
          {
            breakpoint: 2000,
            settings: {
              slidesToShow: 6,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 1800,
            settings: {
              slidesToShow: 6,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 1300,
            settings: {
              slidesToShow: 5,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 800,
            settings: {
              slidesToShow: 4,
              slidesToScroll: 1,
              swipeToSlide: true,
              swipe: true,
            },
          },
          {
            breakpoint: 700,
            settings: {
              slidesToShow: 1,
              slidesToScroll: 1,
              swipeToSlide: true,
              swipe: true,
            },
          },
        ]}
        slidesToScroll={1}
        slidesToShow={7}
        variableWidth={true}
      >
        {featuredItems.map(renderFeaturedItem)}
      </Slider>
    </React.Fragment>
  );
}

export default React.memo(GuestHomeFeaturedSection);
